import { useEffect, useState } from 'react';
import { apiClient } from '@/lib/apiClient';
import { Users, GitBranch, List, Award as AwardIcon, User, Package } from 'lucide-react';
import '../customer.css';

const fmt = (n) => new Intl.NumberFormat('fa-IR', { maximumFractionDigits: 0 }).format(Number(n) || 0);
const fmtPercent = (n) => {
    const val = Number(n);
    if (isNaN(val) || val === 0) return '';
    return val % 1 === 0 ? `${val}٪` : `${parseFloat(val.toFixed(2))}٪`;
};

// ---------------------------------------------------------
// کامپوننت نمایش گرافیکی درخت
// ---------------------------------------------------------
function TreeNodeGraph({ node }) {
    const fullName = [node.first_name, node.last_name].filter(Boolean).join(' ') || 'کاربر بدون نام';
    const totalCommission = Number(node.total_earned) || 0;
    const percent = Number(node.commission_percent) || 0;

    return (
        <li>
            <div className="referral-tree-graph-node" style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

                <div style={{
                    background: node.level === 0 ? '#eff6ff' : '#fff',
                    border: `2px solid ${node.level === 0 ? '#3b82f6' : '#cbd5e1'}`,
                    borderRadius: '8px',
                    padding: '10px 15px',
                    minWidth: '160px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    position: 'relative',
                    zIndex: 2
                }} className="referral-tree-graph-card">
                    <div style={{
                        fontWeight: 'bold',
                        fontSize: '0.95rem',
                        color: node.level === 0 ? '#1e3a8a' : '#1e293b',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px'
                    }}>
                        {node.level === 0 ? <User size={16} /> : null}
                        {fullName}
                    </div>

                    <div style={{
                        fontSize: '0.75rem',
                        background: node.level === 0 ? '#dbeafe' : '#f1f5f9',
                        color: node.level === 0 ? '#1e40af' : '#475569',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        marginTop: '6px',
                        fontWeight: 'bold'
                    }}>
                        سطح {node.level}
                    </div>

                    {(totalCommission > 0 || percent > 0) && (
                        <div style={{
                            fontSize: '0.75rem',
                            color: '#059669',
                            background: '#ecfdf5',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            marginTop: '6px',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                        }}>
                            <AwardIcon size={12} />
                            {percent > 0 ? `${fmtPercent(percent)} | ` : ''}{fmt(totalCommission)} ریال
                        </div>
                    )}
                </div>

                {/* === [REMOVED] کادر توضیحات شناور حذف شد === */}
            </div>

            {node.children && node.children.length > 0 && (
                <ul>
                    {node.children.map(child => (
                        <TreeNodeGraph key={child.user_id} node={child} />
                    ))}
                </ul>
            )}
        </li>
    );
}

// ---------------------------------------------------------
// کامپوننت نمایش متنی درخت
// ---------------------------------------------------------
function TreeNodeList({ node }) {
    const fullName = [node.first_name, node.last_name].filter(Boolean).join(' ') || 'کاربر بدون نام';
    const totalCommission = Number(node.total_earned) || 0;
    const percent = Number(node.commission_percent) || 0;

    return (
        <div className="referral-tree-list-node" style={{ marginRight: '20px', borderRight: '2px solid #e2e8f0', paddingRight: '15px', marginTop: '10px', textAlign: 'right' }}>
            <div className="referral-tree-list-row" style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#fff', padding: '10px 15px', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                <div style={{ background: node.level === 0 ? '#10b981' : '#eff6ff', padding: '8px', borderRadius: '50%' }}>
                    <User size={18} color={node.level === 0 ? '#fff' : '#2563eb'} />
                </div>

                <div className="referral-tree-list-content" style={{ flex: 1 }}>
                    <div style={{ fontWeight: 'bold', color: '#1e293b' }}>
                        {fullName}
                        <span style={{ fontSize: '0.75rem', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', marginRight: '8px' }}>سطح {node.level}</span>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#64748b', direction: 'ltr', textAlign: 'right' }}>{node.mobile_phone}</div>
                </div>

                {(totalCommission > 0 || percent > 0) && (
                    <div style={{ fontSize: '0.8rem', color: '#059669', background: '#ecfdf5', padding: '4px 8px', borderRadius: '6px', border: '1px solid #a7f3d0', display: 'flex', gap: '5px', alignItems: 'center' }}>
                        <AwardIcon size={14} />
                        {percent > 0 ? `${fmtPercent(percent)} | ` : ''}{fmt(totalCommission)} ریال
                    </div>
                )}
            </div>

            {/* === [REMOVED] کادر توضیحات زیر گره‌ها در حالت متنی حذف شد === */}

            {node.children && node.children.length > 0 && (
                <div style={{ marginTop: '5px' }}>
                    {node.children.map(child => (
                        <TreeNodeList key={child.user_id} node={child} />
                    ))}
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------
// کامپوننت اصلی صفحه
// ---------------------------------------------------------
export default function ReferralTreePage() {
    const [packages, setPackages] = useState([]);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [treeData, setTreeData] = useState(null);
    const [loadingPackages, setLoadingPackages] = useState(true);
    const [loadingTree, setLoadingTree] = useState(false);
    const [error, setError] = useState('');
    const [viewMode, setViewMode] = useState('graph');

    useEffect(() => {
        const fetchPackages = async () => {
            try {
                const { data, error } = await apiClient.GET('/api/v1/customer/referral/packages');
                if (error) throw new Error(error.detail || 'خطا در دریافت پکیج‌ها');
                setPackages(data || []);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoadingPackages(false);
            }
        };
        fetchPackages();
    }, []);

    useEffect(() => {
        if (!selectedPackage) {
            setTreeData(null);
            return;
        }

        const fetchTree = async () => {
            setLoadingTree(true);
            setError('');
            try {
                const { data, error } = await apiClient.GET('/api/v1/customer/referral/tree', {
                    params: { query: { max_depth: 5, package_id: selectedPackage.id } }
                });
                if (error) throw new Error(error.detail || 'خطا در دریافت درخت');
                setTreeData(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoadingTree(false);
            }
        };
        fetchTree();
    }, [selectedPackage]);

    if (loadingPackages) return <div style={{ padding: '3rem', textAlign: 'center' }}>در حال بارگذاری...</div>;

    return (
        <div className="customer-profile-page customer-referral-tree-page" style={{ textAlign: 'center' }}>

            <div className="customer-profile-header" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'center' }}>
                <h1 className="customer-profile-title" style={{ fontSize: '1.4rem', margin: 0 }}>درخت معرفی و پورسانت‌ها</h1>
            </div>

            {packages.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                    <Package size={48} color="#94a3b8" style={{ margin: '0 auto 1rem' }} />
                    <p style={{ color: '#64748b' }}>شما هنوز هیچ پکیج فروش گروهی نخریده‌اید.</p>
                </div>
            ) : (
                <div>
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: '0.75rem',
                        marginBottom: '2rem'
                    }}>
                        {packages.map((pkg) => {
                            const isSelected = selectedPackage?.id === pkg.id;
                            return (
                                <div
                                    className="referral-tree-package-option"
                                    key={pkg.id}
                                    onClick={() => {
                                        setSelectedPackage(pkg);
                                        setViewMode('graph');
                                    }}
                                    style={{
                                        background: isSelected ? '#d1fae5' : '#ecfdf5',
                                        border: `2px solid ${isSelected ? '#10b981' : '#a7f3d0'}`,
                                        borderRadius: '8px',
                                        padding: '1rem 1.5rem',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        fontSize: '0.9rem',
                                        fontWeight: isSelected ? 'bold' : 'normal',
                                        color: isSelected ? '#065f46' : '#047857',
                                        minWidth: '150px'
                                    }}
                                >
                                    {pkg.name}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {selectedPackage && (
                <div style={{ marginTop: '2rem', borderTop: '2px dashed #cbd5e1', paddingTop: '2rem' }}>

                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '1.5rem', gap: '1rem' }}>

                        <h2 style={{ fontSize: '1.2rem', color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Users size={20} color="#2563eb" /> لیست افراد معرفی شده
                        </h2>

                        <div style={{ display: 'flex', width: '100%', gap: '0.5rem', background: '#f1f5f9', padding: '0.3rem', borderRadius: '8px' }}>
                            <button
                                className="referral-tree-view-toggle"
                                onClick={() => setViewMode('graph')}
                                style={{
                                    flex: 1,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '0.5rem 0.8rem', borderRadius: '6px', border: 'none',
                                    background: viewMode === 'graph' ? '#fff' : 'transparent',
                                    color: viewMode === 'graph' ? '#1e293b' : '#64748b',
                                    boxShadow: viewMode === 'graph' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                    cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem'
                                }}
                            >
                                <GitBranch size={16} /> نمایش درختی
                            </button>
                            <button
                                className="referral-tree-view-toggle"
                                onClick={() => setViewMode('list')}
                                style={{
                                    flex: 1,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '0.5rem 0.8rem', borderRadius: '6px', border: 'none',
                                    background: viewMode === 'list' ? '#fff' : 'transparent',
                                    color: viewMode === 'list' ? '#1e293b' : '#64748b',
                                    boxShadow: viewMode === 'list' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                    cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem'
                                }}
                            >
                                <List size={16} /> نمایش متنی
                            </button>
                        </div>
                    </div>

                    {loadingTree && <div style={{ padding: '2rem', textAlign: 'center' }}>در حال بارگذاری درخت...</div>}
                    {error && <div style={{ padding: '2rem', textAlign: 'center', color: 'red' }}>{error}</div>}

                    {!loadingTree && !error && treeData && (
                        <>
                            {treeData.total_descendants === 0 ? (
                                <div style={{ textAlign: 'center', padding: '3rem', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                    <Users size={48} color="#94a3b8" style={{ margin: '0 auto 1rem' }} />
                                    <p style={{ color: '#64748b', fontSize: '1.05rem' }}>هنوز کسی با کد معرف شما از این پکیج خرید نکرده است.</p>
                                </div>
                            ) : (
                                <div className="referral-tree-graph-frame" style={{ background: '#f8fafc', padding: '1.5rem', borderRadius: '12px', border: '1px solid #e2e8f0', overflowX: 'hidden', minHeight: '300px' }}>
                                    {viewMode === 'graph' ? (
                                        <div className="org-tree" style={{ direction: 'rtl' }}>
                                            <ul>
                                                <TreeNodeGraph node={treeData.root} />
                                            </ul>
                                            <style>{`
                                  .org-tree { max-width: 100%; overflow-x: auto; overflow-y: hidden; padding-top: 20px; overscroll-behavior-inline: contain; touch-action: pan-x pan-y; -webkit-overflow-scrolling: touch; }
                                  .org-tree ul { padding-top: 20px; position: relative; display: flex; justify-content: center; margin: 0; list-style: none; }
                                  .org-tree li { list-style: none; position: relative; padding: 20px 10px 0 10px; display: flex; flex-direction: column; align-items: center; }
                                  .org-tree li::before, .org-tree li::after { content: ''; position: absolute; top: 0; right: 50%; border-top: 2px solid #94a3b8; width: 50%; height: 20px; z-index: 0; }
                                  .org-tree li::after { right: auto; left: 50%; border-left: 2px solid #94a3b8; }
                                  .org-tree li:only-child::after, .org-tree li:only-child::before { display: none; }
                                  .org-tree li:only-child { padding-top: 0; }
                                  /* چون جهت صفحه راست‌به‌چپ است: اولین فرزند راست‌ترین و آخرین فرزند چپ‌ترین است */
                                  .org-tree li:first-child::after, .org-tree li:last-child::before { border: 0 none; }
                                  .org-tree li:first-child::before { border-right: 2px solid #94a3b8; border-radius: 0 5px 0 0; }
                                  .org-tree li:last-child::after { border-radius: 5px 0 0 0; }
                                  .org-tree ul ul::before { content: ''; position: absolute; top: 0; left: 50%; border-left: 2px solid #94a3b8; width: 0; height: 20px; transform: translateX(-50%); z-index: 0; }
                                `}</style>
                                        </div>
                                    ) : (
                                        <div className="referral-tree-list-frame" style={{ display: 'flex', justifyContent: 'center' }}>
                                            <TreeNodeList node={treeData.root} />
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
